import React, { useContext, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { Box, Stack, Toolbar, Typography, IconButton } from '@mui/material';
import RightDrawer, { RightDrawerProps } from './RightDrawer';
import LeftDrawer, { LeftDrawerProps } from './LeftDrawer';
import MenuBar from '_lex/_atoms/MenuBar/index';
import PanelToolbar from '_lex/_atoms/PanelToolbar/index';
import NavigationHeader, { BackNavIconVariant } from '_lex/_atoms/NavigationHeader/index';
import { PageHeader, PageHeaderProps } from '_lex/_atoms/PageHeader/PageHeader';
import { PageToolbar } from '_lex/_atoms/PageToolbar';
import TruncatedText from '_lex/_atoms/TruncatedText';
import _ from 'lodash';
import { translate as t } from 'hooks/useTranslations';
import { neutral } from '_lex/_foundations/phxColors';

export type TakeoverLayoutProps = {
  backUrl?: string;
  backNavIcon?: BackNavIconVariant;
  backNavAriaLabel?: string;
  children?: React.ReactNode;
  footerContent?: React.ReactNode;
  contentHeaderLeft?: React.ReactNode;
  contentHeaderRight?: React.ReactNode;
  contentBackground?: 'white' | 'neutral';
  onBackClick?: () => void;
  pageTitle?: React.ReactNode;
  pageActions?: React.ReactNode;
  banner?: React.ReactNode;
  menubarLeft?: React.ReactNode;
  menubarRight?: React.ReactNode;
  pageToolbarLeft?: React.ReactNode;
  pageToolbarRight?: React.ReactNode;
  leftDrawer?: (Omit<LeftDrawerProps, 'open' | 'setOpen'> & { closeable?: boolean; }) | React.ReactNode;
  rightDrawer?: (Omit<RightDrawerProps, 'open' | 'setOpen'> & { closeable?: boolean; }) | React.ReactNode;
  leftDrawerOpen?: boolean;
  setLeftDrawerOpen?: (open: boolean) => void;
  rightDrawerOpen?: boolean;
  setRightDrawerOpen?: (open: boolean) => void;
  noPadding?: boolean;
  headerProps?: PageHeaderProps;
};

export const TakeoverLayout: React.FC<TakeoverLayoutProps> = ({
  backUrl,
  backNavIcon,
  backNavAriaLabel,
  children = [],
  footerContent,
  contentHeaderLeft,
  contentHeaderRight,
  contentBackground = 'white',
  onBackClick,
  pageTitle,
  pageActions,
  banner,
  menubarLeft,
  menubarRight,
  pageToolbarLeft,
  pageToolbarRight,
  leftDrawer,
  rightDrawer,
  leftDrawerOpen: externalLeftDrawerOpen,
  setLeftDrawerOpen: externalSetLeftDrawerOpen,
  rightDrawerOpen: externalRightDrawerOpen,
  setRightDrawerOpen: externalSetRightDrawerOpen,
  noPadding = false,
  headerProps,
}: TakeoverLayoutProps) => {
  const [internalLeftDrawerOpen, setInternalLeftDrawerOpen] = React.useState(true);
  const [internalRightDrawerOpen, setInternalRightDrawerOpen] = React.useState(true);

  const leftDrawerOpen = externalLeftDrawerOpen ?? internalLeftDrawerOpen;
  const setLeftDrawerOpen = externalSetLeftDrawerOpen ?? setInternalLeftDrawerOpen;
  const rightDrawerOpen = externalRightDrawerOpen ?? internalRightDrawerOpen;
  const setRightDrawerOpen = externalSetRightDrawerOpen ?? setInternalRightDrawerOpen;

  const navigate = useNavigate();

  React.useEffect(() => {
    const titleText = typeof pageTitle === 'string' ? pageTitle : (pageTitle as any)?.props?.children || '';
    if (!_.isEmpty(titleText)) {
      document.title = titleText;
    } else {
      document.title = t('common.browserTitle');
    }
  }, [pageTitle]);

  const handleBack = async () => {
    if (onBackClick) {
      await onBackClick();
    }
  };

  const gridColumns = () => {
    let gridCols = 'minmax(0, 1fr)';
    if (!!leftDrawer) gridCols = `auto ${gridCols}`;
    if (!!rightDrawer) gridCols = `${gridCols} auto`;

    return gridCols;
  };

  const contentRows = () => {
    let gridRows = '1fr';
    if (headerProps) gridRows = `auto ${gridRows}`;
    if (contentHeaderLeft || contentHeaderRight) gridRows = `auto ${gridRows}`;
    if (footerContent) gridRows = `${gridRows} auto`;

    return gridRows;
  };

  return (
    <Box
      className='lex-detail_page'
      sx={{
        display: 'grid',
        gridTemplateRows: 'auto auto 1fr',
        gridTemplateColumns: 'minmax(0, 1fr)',
        height: '100vh',
        maxHeight: '100vh',
      }}
    >
      <NavigationHeader
        backUrl={backUrl}
        backNavIcon={backNavIcon}
        backNavAriaLabel={backNavAriaLabel}
        onBackClick={onBackClick ? handleBack : undefined}
        leftItems={
          typeof pageTitle === 'string' ? (
            <TruncatedText
              text={pageTitle}
              typographyProps={{ variant: 'subtitleLg' }}
            />
          ) : (
            <Typography variant='subtitleLg'>{pageTitle}</Typography>
          )
        }
        rightItems={pageActions}
      />
      {(banner || menubarLeft || menubarRight) && (
        <Box
          className='lex-detail_container-toolbar'
          sx={{ gridRow: '2', display: 'flex', flexDirection: 'column' }}
        >
          {banner && <Box sx={{ width: '100%' }}>{banner}</Box>}
          {(menubarLeft || menubarRight) && (
            <MenuBar
              leftItems={menubarLeft}
              rightItems={menubarRight}
            />
          )}
        </Box>
      )}
      <Box
        className='lex-detail_container'
        sx={{
          display: 'grid',
          gridTemplateColumns: gridColumns(),
          gridRow: '3',
          height: '100%',
          overflow: 'hidden',
        }}
      >
        {!!leftDrawer &&
          (typeof leftDrawer === 'object' && 'content' in leftDrawer ? (
            <LeftDrawer
              {...leftDrawer}
              open={leftDrawerOpen}
              setOpen={leftDrawer.closeable !== false ? setLeftDrawerOpen : undefined}
            />
          ) : (
            leftDrawer
          ))}
        <Box
          className='lex-detail_main'
          sx={{
            background: contentBackground === 'white' ? neutral[25] : neutral[50],
            display: 'grid',
            gridTemplateRows: contentRows(),
            gridTemplateColumns: 'minmax(0, 1fr)',
            height: '100%',
            overflow: 'hidden',
          }}
        >
          {headerProps && <PageHeader {...headerProps} />}
          {(contentHeaderLeft || contentHeaderRight) && (
            <PanelToolbar
              leftItems={contentHeaderLeft}
              rightItems={contentHeaderRight}
            />
          )}
          <Box
            className='lex-detail_content'
            sx={{
              p: noPadding ? 0 : 4,
              minHeight: 0,
              minWidth: 0,
              margin: '0 auto',
              display: 'grid',
              gridTemplateRows: pageToolbarLeft || pageToolbarRight ? 'auto 1fr' : '1fr',
              rowGap: noPadding ? 0 : 4,
              width: '100%',
              maxWidth: '100%',
            }}
          >
            {(pageToolbarLeft || pageToolbarRight) && (
              <PageToolbar
                leftItems={pageToolbarLeft}
                rightItems={pageToolbarRight}
              />
            )}
            <Box
              sx={{
                minHeight: 0,
                minWidth: 0,
                overflowX: 'hidden',
                overflowY: 'auto',
                display: 'grid',
                gridTemplateRows: '1fr',
                gridTemplateColumns: '1fr',
              }}
            >
              {children}
            </Box>
          </Box>
          {footerContent && (
            <Toolbar
              className='lex-panel-header'
              sx={{
                borderTop: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Stack
                direction='row'
                spacing={2}
                alignItems='center'
                sx={{ flexGrow: 1 }}
              >
                {footerContent}
              </Stack>
            </Toolbar>
          )}
        </Box>
        {!!rightDrawer &&
          (typeof rightDrawer === 'object' && 'content' in rightDrawer ? (
            <RightDrawer
              {...rightDrawer}
              open={rightDrawerOpen}
              setOpen={rightDrawer.closeable !== false ? setRightDrawerOpen : undefined}
            />
          ) : (
            rightDrawer
          ))}
      </Box>
    </Box>
  );
};

export default TakeoverLayout;
